import React, { useState, useRef, useEffect, useCallback } from "react";
import api from "../api";

const ASPECTS = [
  { label: "자유", value: null }, { label: "1:1", value: 1 },
  { label: "4:3", value: 4/3 }, { label: "3:4", value: 3/4 },
  { label: "16:9", value: 16/9 }, { label: "9:16", value: 9/16 },
  { label: "2:1", value: 2 }, { label: "21:9", value: 21/9 },
];
const SIZE_PRESETS = [
  { label: "원본", w: 0 }, { label: "480", w: 480 }, { label: "720", w: 720 },
  { label: "960", w: 960 }, { label: "1200", w: 1200 }, { label: "1920", w: 1920 },
];

export default function ImageEditorModal({ imageUrl, onSave, onClose }) {
  const canvasRef = useRef(null);
  const [img, setImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturate, setSaturate] = useState(0);
  const [outW, setOutW] = useState(0);
  const [outH, setOutH] = useState(0);
  const [lockRatio, setLockRatio] = useState(true);
  const [viewZoom, setViewZoom] = useState(1);
  const [tab, setTab] = useState("crop");
  const [cropRect, setCropRect] = useState(null);
  const [cropAspect, setCropAspect] = useState(null);
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);
  const cropRef = useRef(null);
  useEffect(() => { dragRef.current = dragState; }, [dragState]);
  useEffect(() => { cropRef.current = cropRect; }, [cropRect]);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => { setImg(image); setError(null); setOutW(image.naturalWidth); setOutH(image.naturalHeight); };
    image.onerror = () => setError("이미지를 불러올 수 없습니다");
    image.src = imageUrl;
  }, [imageUrl]);

  const getSize = useCallback(() => {
    if (!img) return { w: 0, h: 0 };
    const rot = rotation === 90 || rotation === 270;
    return { w: rot ? img.naturalHeight : img.naturalWidth, h: rot ? img.naturalWidth : img.naturalHeight };
  }, [img, rotation]);

  const nativeRatio = img ? img.naturalWidth / img.naturalHeight : 1;

  const getFilter = useCallback(() => {
    const f = [];
    if (brightness !== 0) f.push(`brightness(${1 + brightness/100})`);
    if (contrast !== 0) f.push(`contrast(${1 + contrast/100})`);
    if (saturate !== 0) f.push(`saturate(${1 + saturate/100})`);
    return f.length ? f.join(" ") : "none";
  }, [brightness, contrast, saturate]);

  const draw = useCallback(() => {
    const c = canvasRef.current; if (!c || !img) return;
    const { w, h } = getSize(); c.width = w; c.height = h;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, w, h);
    ctx.save(); ctx.filter = getFilter();
    ctx.translate(w/2, h/2); ctx.rotate((rotation*Math.PI)/180);
    ctx.scale(flipH?-1:1, flipV?-1:1);
    ctx.drawImage(img, -img.naturalWidth/2, -img.naturalHeight/2); ctx.restore();
    if (tab === "crop" && cropRect && cropRect.w > 0 && cropRect.h > 0) {
      const {x,y,w:cw,h:ch} = cropRect; ctx.save();
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(0,0,w,y); ctx.fillRect(0,y+ch,w,h-y-ch);
      ctx.fillRect(0,y,x,ch); ctx.fillRect(x+cw,y,w-x-cw,ch);
      ctx.strokeStyle="#fff"; ctx.lineWidth=2; ctx.strokeRect(x,y,cw,ch);
      ctx.strokeStyle="rgba(255,255,255,0.3)"; ctx.lineWidth=0.5;
      for(let i=1;i<3;i++){const gx=x+(cw*i)/3,gy=y+(ch*i)/3;
        ctx.beginPath();ctx.moveTo(gx,y);ctx.lineTo(gx,y+ch);ctx.stroke();
        ctx.beginPath();ctx.moveTo(x,gy);ctx.lineTo(x+cw,gy);ctx.stroke();}
      ctx.fillStyle="#fff";ctx.shadowColor="rgba(0,0,0,0.5)";ctx.shadowBlur=4;
      for(const[cx2,cy2]of[[x,y],[x+cw,y],[x,y+ch],[x+cw,y+ch]])ctx.fillRect(cx2-5,cy2-5,10,10);
      ctx.shadowBlur=0; ctx.font="bold 11px system-ui"; ctx.fillStyle="rgba(255,255,255,0.85)"; ctx.textAlign="center";
      ctx.fillText(`${Math.round(cw)} \u00d7 ${Math.round(ch)}`,x+cw/2,y>20?y-8:y+ch+16); ctx.restore();
    }
  }, [img, rotation, flipH, flipV, getFilter, tab, cropRect, getSize]);
  useEffect(() => { draw(); }, [draw]);

  const toCanvas = useCallback((e) => {
    const c = canvasRef.current; if (!c) return {x:0,y:0};
    const r = c.getBoundingClientRect();
    return {x:(e.clientX-r.left)*(c.width/r.width), y:(e.clientY-r.top)*(c.height/r.height)};
  }, []);
  const hitTest = useCallback((cx,cy) => {
    const cr = cropRef.current; if (!cr) return null;
    const {x,y,w,h} = cr; const m=15;
    const onL=Math.abs(cx-x)<m,onR=Math.abs(cx-(x+w))<m,onT=Math.abs(cy-y)<m,onB=Math.abs(cy-(y+h))<m;
    if(onT&&onL)return"nw-resize";if(onT&&onR)return"ne-resize";
    if(onB&&onL)return"sw-resize";if(onB&&onR)return"se-resize";
    if(onT&&cx>x&&cx<x+w)return"n-resize";if(onB&&cx>x&&cx<x+w)return"s-resize";
    if(onL&&cy>y&&cy<y+h)return"w-resize";if(onR&&cy>y&&cy<y+h)return"e-resize";
    if(cx>x&&cx<x+w&&cy>y&&cy<y+h)return"move"; return null;
  }, []);

  const onDown = useCallback((e) => {
    if (tab !== "crop") return; e.preventDefault();
    const {x,y} = toCanvas(e); const hit = hitTest(x,y);
    if(hit){const ds={type:hit,sx:x,sy:y,orig:{...cropRef.current}};setDragState(ds);dragRef.current=ds;}
    else{setCropRect({x,y,w:0,h:0});cropRef.current={x,y,w:0,h:0};const ds={type:"new",sx:x,sy:y};setDragState(ds);dragRef.current=ds;}
  }, [tab, toCanvas, hitTest]);

  const onMove = useCallback((e) => {
    const ds = dragRef.current;
    if(!ds){if(tab==="crop"&&canvasRef.current){const{x,y}=toCanvas(e);const hit=hitTest(x,y);canvasRef.current.style.cursor=hit?(hit==="move"?"move":hit):"crosshair";}return;}
    e.preventDefault();
    const {x:cx,y:cy} = toCanvas(e); const {w:cW,h:cH} = getSize();
    const {type,sx,sy,orig} = ds;
    const cl = (v,lo,hi) => Math.max(lo,Math.min(hi,v));
    if(type==="new"){
      let nx=Math.min(sx,cx),ny=Math.min(sy,cy),nw=Math.abs(cx-sx),nh=Math.abs(cy-sy);
      if(cropAspect){if(nw/Math.max(nh,1)>cropAspect)nw=nh*cropAspect;else nh=nw/cropAspect;if(cx<sx)nx=sx-nw;if(cy<sy)ny=sy-nh;}
      nx=cl(nx,0,cW);ny=cl(ny,0,cH);nw=Math.min(nw,cW-nx);nh=Math.min(nh,cH-ny);
      const r={x:nx,y:ny,w:nw,h:nh};setCropRect(r);cropRef.current=r;
    } else if(type==="move"){
      const r={...orig,x:cl(orig.x+cx-sx,0,cW-orig.w),y:cl(orig.y+cy-sy,0,cH-orig.h)};setCropRect(r);cropRef.current=r;
    } else {
      let{x:rx,y:ry,w:rw,h:rh}=orig; const dx=cx-sx,dy=cy-sy;
      if(type.includes("e"))rw=Math.max(20,orig.w+dx);
      if(type.includes("w")){rx=orig.x+dx;rw=Math.max(20,orig.w-dx);}
      if(type.includes("s"))rh=Math.max(20,orig.h+dy);
      if(type.includes("n")){ry=orig.y+dy;rh=Math.max(20,orig.h-dy);}
      if(cropAspect){if(type==="n-resize"||type==="s-resize")rw=rh*cropAspect;else if(type==="w-resize"||type==="e-resize")rh=rw/cropAspect;else{if(rw/rh>cropAspect)rh=rw/cropAspect;else rw=rh*cropAspect;}}
      rx=cl(rx,0,cW);ry=cl(ry,0,cH);rw=Math.min(rw,cW-rx);rh=Math.min(rh,cH-ry);
      const r={x:rx,y:ry,w:rw,h:rh};setCropRect(r);cropRef.current=r;
    }
  }, [tab, toCanvas, hitTest, getSize, cropAspect]);

  const onUp = useCallback(() => { setDragState(null); dragRef.current = null; }, []);
  // Use ref-based wheel listener to avoid passive event warning
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const handler = (e) => { e.preventDefault(); setViewZoom(z => Math.max(0.25, Math.min(5, z + (e.deltaY < 0 ? 0.15 : -0.15)))); };
    c.addEventListener('wheel', handler, { passive: false });
    return () => c.removeEventListener('wheel', handler);
  }, [img]);

  const applyCrop = useCallback(() => {
    if(!cropRect||cropRect.w<5||cropRect.h<5||!img)return;
    const {w,h}=getSize(); const full=document.createElement("canvas"); full.width=w;full.height=h;
    const fCtx=full.getContext("2d"); fCtx.filter=getFilter(); fCtx.translate(w/2,h/2);
    fCtx.rotate((rotation*Math.PI)/180); fCtx.scale(flipH?-1:1,flipV?-1:1);
    fCtx.drawImage(img,-img.naturalWidth/2,-img.naturalHeight/2);
    const cw=Math.round(cropRect.w),ch=Math.round(cropRect.h);
    const crop=document.createElement("canvas"); crop.width=cw;crop.height=ch;
    crop.getContext("2d").drawImage(full,cropRect.x,cropRect.y,cropRect.w,cropRect.h,0,0,cw,ch);
    const ni=new Image(); ni.onload=()=>{setImg(ni);setRotation(0);setFlipH(false);setFlipV(false);
      setBrightness(0);setContrast(0);setSaturate(0);setCropRect(null);cropRef.current=null;setOutW(cw);setOutH(ch);};
    ni.src=crop.toDataURL("image/png");
  }, [cropRect, img, rotation, flipH, flipV, getFilter, getSize]);

  const resetAll = () => {
    setRotation(0);setFlipH(false);setFlipV(false);setBrightness(0);setContrast(0);setSaturate(0);
    setCropRect(null);cropRef.current=null;setViewZoom(1);
    const i2=new Image();i2.crossOrigin="anonymous";
    i2.onload=()=>{setImg(i2);setOutW(i2.naturalWidth);setOutH(i2.naturalHeight);};i2.src=imageUrl;
  };
  const handleOutW = (v) => { const w=Math.max(1,Math.round(Number(v)||1)); setOutW(w); if(lockRatio)setOutH(Math.round(w/nativeRatio)); };
  const handleOutH = (v) => { const h=Math.max(1,Math.round(Number(v)||1)); setOutH(h); if(lockRatio)setOutW(Math.round(h*nativeRatio)); };

  const handleSave = useCallback(async () => {
    if(!img)return; setSaving(true);
    try {
      const {w,h}=getSize(); const off=document.createElement("canvas"); off.width=w;off.height=h;
      const ctx=off.getContext("2d"); ctx.filter=getFilter(); ctx.translate(w/2,h/2);
      ctx.rotate((rotation*Math.PI)/180); ctx.scale(flipH?-1:1,flipV?-1:1);
      ctx.drawImage(img,-img.naturalWidth/2,-img.naturalHeight/2);
      let src=off;
      if(tab==="crop"&&cropRect&&cropRect.w>10&&cropRect.h>10){
        const c2=document.createElement("canvas");c2.width=Math.round(cropRect.w);c2.height=Math.round(cropRect.h);
        c2.getContext("2d").drawImage(off,cropRect.x,cropRect.y,cropRect.w,cropRect.h,0,0,c2.width,c2.height);src=c2;}
      const fw=outW||src.width,fh=outH||src.height;
      let fin=src;
      if(fw!==src.width||fh!==src.height){const rs=document.createElement("canvas");rs.width=fw;rs.height=fh;rs.getContext("2d").drawImage(src,0,0,fw,fh);fin=rs;}
      const big=fw*fh>1500*1500; const fmt=big?"image/webp":"image/png"; const q=big?0.85:undefined;
      const blob=await new Promise(r=>fin.toBlob(r,fmt,q));
      const fd=new FormData(); fd.append("image",blob,big?"edited.webp":"edited.png");
      const result=await api.post("/api/admin/upload-image",fd,{headers:{"Content-Type":"multipart/form-data"}});
      onSave(result.data.url); onClose();
    } catch(err){ alert("저장 실패: "+(err.response?.data?.error||err.message)); }
    finally{ setSaving(false); }
  }, [img, rotation, flipH, flipV, getFilter, getSize, tab, cropRect, outW, outH, onSave, onClose]);

  useEffect(() => { const h=(e)=>{if(e.key==="Escape")onClose();}; window.addEventListener("keydown",h); return()=>window.removeEventListener("keydown",h); }, [onClose]);

  const hasChanges = rotation!==0||flipH||flipV||brightness!==0||contrast!==0||saturate!==0||
    (cropRect&&cropRect.w>10&&cropRect.h>10)||(img&&(outW!==img.naturalWidth||outH!==img.naturalHeight));
  if(!imageUrl)return null;
  const {w:srcW,h:srcH}=getSize();

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e)=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" onClick={(e)=>e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50/80 shrink-0">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800 transition font-medium">취소</button>
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-gray-900">이미지 편집</h3>
            {img && <span className="text-[10px] text-gray-400 tabular-nums">{srcW} × {srcH}px</span>}
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={resetAll} className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1 transition rounded-lg hover:bg-gray-100">↩ 초기화</button>
            <button onClick={handleSave} disabled={saving||!hasChanges} className="px-4 py-1.5 bg-[#0071e3] text-white text-sm font-semibold rounded-lg hover:bg-[#0077ED] disabled:opacity-40 transition">
              {saving ? "저장 중..." : "저장"}</button>
          </div>
        </div>
        {/* Canvas */}
        <div className="flex-1 min-h-0 flex items-center justify-center p-4 bg-[#1d1d1f] overflow-auto relative">
          {error ? <p className="text-red-400 text-sm">{error}</p> : !img ? <p className="text-gray-400 text-sm animate-pulse">이미지 로딩 중...</p> :
            <canvas ref={canvasRef} className="rounded-lg shadow-lg"
              style={{cursor:tab==="crop"?"crosshair":"default",maxWidth:"100%",maxHeight:"52vh",transform:`scale(${viewZoom})`,transformOrigin:"center center"}}
              onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp} />}
          <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/60 backdrop-blur rounded-lg px-2 py-1">
            <button onClick={()=>setViewZoom(z=>Math.max(0.25,z-0.25))} className="text-white/80 hover:text-white text-sm w-5 h-5 flex items-center justify-center">−</button>
            <span className="text-white/80 text-[10px] tabular-nums w-10 text-center">{Math.round(viewZoom*100)}%</span>
            <button onClick={()=>setViewZoom(z=>Math.min(5,z+0.25))} className="text-white/80 hover:text-white text-sm w-5 h-5 flex items-center justify-center">+</button>
            <button onClick={()=>setViewZoom(1)} className="text-white/50 hover:text-white text-[9px] ml-1">맞춤</button>
          </div>
        </div>
        {/* Tools */}
        <div className="border-t border-gray-200 shrink-0">
          <div className="flex border-b border-gray-100">
            {[{key:"crop",icon:"✂️",label:"자르기"},{key:"resize",icon:"📐",label:"크기"},{key:"transform",icon:"🔄",label:"회전"},{key:"adjust",icon:"🌗",label:"보정"}].map(({key,icon,label})=>(
              <button key={key} onClick={()=>setTab(key)} className={`flex-1 py-2.5 text-xs font-semibold transition ${tab===key?"text-[#0071e3] border-b-2 border-[#0071e3] bg-blue-50/50":"text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}>{icon} {label}</button>
            ))}
          </div>
          <div className="px-5 py-3 min-h-[80px] flex items-center">
            {tab==="crop" && (
              <div className="flex items-center gap-2 flex-wrap w-full">
                <span className="text-xs text-gray-500 font-medium shrink-0">비율:</span>
                {ASPECTS.map(({label:lb,value})=>(<button key={lb} onClick={()=>{setCropAspect(value);setCropRect(null);cropRef.current=null;}}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition ${cropAspect===value?"bg-[#0071e3] text-white shadow-sm":"bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{lb}</button>))}
                <div className="flex-1" />
                {cropRect&&cropRect.w>10&&cropRect.h>10&&(<button onClick={applyCrop} className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition shadow-sm">✓ 자르기 적용</button>)}
              </div>)}
            {tab==="resize" && (
              <div className="w-full space-y-2.5">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <label className="text-[11px] text-gray-500 font-medium">가로</label>
                    <input type="number" value={outW} onChange={(e)=>handleOutW(e.target.value)} className="w-20 px-2 py-1.5 text-xs border border-gray-200 rounded-lg text-center tabular-nums focus:ring-1 focus:ring-blue-400 focus:outline-none" />
                    <span className="text-[10px] text-gray-400">px</span>
                  </div>
                  <button onClick={()=>setLockRatio(!lockRatio)} className={`w-7 h-7 rounded-md flex items-center justify-center text-sm transition ${lockRatio?"bg-[#0071e3] text-white":"bg-gray-100 text-gray-400"}`}>{lockRatio?"🔗":"🔓"}</button>
                  <div className="flex items-center gap-1.5">
                    <label className="text-[11px] text-gray-500 font-medium">세로</label>
                    <input type="number" value={outH} onChange={(e)=>handleOutH(e.target.value)} className="w-20 px-2 py-1.5 text-xs border border-gray-200 rounded-lg text-center tabular-nums focus:ring-1 focus:ring-blue-400 focus:outline-none" />
                    <span className="text-[10px] text-gray-400">px</span>
                  </div>
                  <span className="text-[10px] text-gray-400 ml-2">→ {outW}×{outH}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-gray-400 mr-1">프리셋:</span>
                  {SIZE_PRESETS.map(({label:lb,w:pw})=>(<button key={lb} onClick={()=>{if(pw===0)handleOutW(img?.naturalWidth||1);else handleOutW(pw);}}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition ${(pw===0&&outW===(img?.naturalWidth||0))||(pw>0&&outW===pw)?"bg-[#0071e3] text-white":"bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{lb}</button>))}
                </div>
              </div>)}
            {tab==="transform" && (
              <div className="flex items-center gap-2 justify-center w-full">
                <button onClick={()=>{setRotation(r=>(r+270)%360);setCropRect(null);cropRef.current=null;}} className="w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xl transition active:scale-95">↺</button>
                <button onClick={()=>{setRotation(r=>(r+90)%360);setCropRect(null);cropRef.current=null;}} className="w-12 h-12 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xl transition active:scale-95">↻</button>
                <div className="w-px h-8 bg-gray-200 mx-1" />
                <button onClick={()=>{setFlipH(v=>!v);setCropRect(null);cropRef.current=null;}} className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition active:scale-95 ${flipH?"bg-[#0071e3] text-white shadow-sm":"bg-gray-100 hover:bg-gray-200"}`}>↔</button>
                <button onClick={()=>{setFlipV(v=>!v);setCropRect(null);cropRef.current=null;}} className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl transition active:scale-95 ${flipV?"bg-[#0071e3] text-white shadow-sm":"bg-gray-100 hover:bg-gray-200"}`}>↕</button>
                <div className="ml-3 text-xs text-gray-400 space-x-2">{rotation>0&&<span>{rotation}°</span>}{flipH&&<span>좌우</span>}{flipV&&<span>상하</span>}</div>
              </div>)}
            {tab==="adjust" && (
              <div className="space-y-2 w-full max-w-lg mx-auto">
                {[{l:"밝기",v:brightness,s:setBrightness},{l:"대비",v:contrast,s:setContrast},{l:"채도",v:saturate,s:setSaturate}].map(({l,v,s})=>(
                  <div key={l} className="flex items-center gap-3">
                    <label className="text-xs font-medium text-gray-500 w-8 shrink-0">{l}</label>
                    <input type="range" min={-100} max={100} value={v} onChange={e=>s(Number(e.target.value))} className="flex-1 accent-[#0071e3] h-1.5" />
                    <span className="text-xs text-gray-500 w-8 text-right tabular-nums">{v}</span>
                    {v!==0&&<button onClick={()=>s(0)} className="text-xs text-gray-400 hover:text-gray-600">↩</button>}
                  </div>))}
              </div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
